import { useQueries } from '@tanstack/react-query';
import { BarChart3, LineChart } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  getDiscussoesControllerObterSerieDoAnalitoDoHistoricoQueryOptions,
  useDiscussoesControllerObterResumoDoHistorico,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import type { AnalitoDoHistoricoResponse, SerieDoAnalitoResponse } from '@/api/generated/model';
import { GraficoDeBarrasDoExame } from '@/features/historico/components/GraficoDeBarrasDoExame';
import { GraficoDeEvolucao } from '@/features/historico/components/GraficoDeEvolucao';
import { SeletorDeAnalito } from '@/features/historico/components/SeletorDeAnalito';
import { TabelaComparativa } from '@/features/historico/components/TabelaComparativa';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';

/** `exame` = barras agrupadas com todos os analitos; `analito` = a linha de um só. */
type Modo = 'exame' | 'analito';

interface Props {
  discussaoId: string;
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * Evolução dos exames do paciente da discussão — mesma comparação (barras
 * agrupadas por exame do catálogo, ou linha de um analito só) que
 * `AbaEvolucao` mostra pro médico na tela de histórico, liberada ao advisor
 * por participação na discussão, não por tenant.
 *
 * ⚠️ Duplica a orquestração de `AbaEvolucao` em vez de parametrizá-la: muda
 * de onde a série vem (rota escopada por participação), mas a REGRA do
 * gráfico é a mesma e não pode divergir — barras agrupadas só quando o
 * exame do catálogo tem MAIS DE UM analito medido; com um resultado só, cai
 * pra linha, sem depender do que a pessoa escolheu antes.
 */
export function EvolucaoDoPacienteDialogo({ discussaoId, aberto, aoFechar }: Props) {
  const resumo = useDiscussoesControllerObterResumoDoHistorico(discussaoId, {
    query: { enabled: aberto },
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Evolução dos exames</DialogTitle>
        </DialogHeader>

        {resumo.isPending ? (
          <Skeleton className="h-64 rounded-md" />
        ) : resumo.isError || !resumo.data ? (
          <EstadoErro aoTentarDeNovo={() => void resumo.refetch()} />
        ) : resumo.data.analitos.length === 0 ? (
          <EstadoVazio
            icone={LineChart}
            titulo="Nenhum resultado lançado"
            descricao="A evolução aparece quando houver exames concluídos com resultados."
          />
        ) : (
          <ConteudoDaEvolucao discussaoId={discussaoId} analitos={resumo.data.analitos} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConteudoDaEvolucao({
  discussaoId,
  analitos,
}: {
  discussaoId: string;
  analitos: AnalitoDoHistoricoResponse[];
}) {
  const [analitoId, setAnalitoId] = useState('');
  const [modo, setModo] = useState<Modo>('exame');

  // O primeiro da lista é o de medição mais recente — é o que se quer ver ao
  // abrir. Só depois de escolher é que `analitoId` manda.
  const selecionadoId = analitoId || analitos[0]?.analitoId || '';
  const selecionado = analitos.find((a) => a.analitoId === selecionadoId);

  // Os analitos do MESMO exame do catálogo, na ordem em que o laudo os imprime.
  const doExame = useMemo(() => {
    if (!selecionado) return [];

    return analitos
      .filter((a) => a.catalogoExameId === selecionado.catalogoExameId)
      .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [analitos, selecionado]);

  // Sem um segundo analito não existe barra agrupada para desenhar: o modo
  // cai para a linha, sem depender do que a pessoa escolheu antes.
  const modoEfetivo: Modo = doExame.length > 1 ? modo : 'analito';

  const alvos = modoEfetivo === 'exame' ? doExame : selecionado ? [selecionado] : [];

  const consultas = useQueries({
    queries: alvos.map((analito) =>
      getDiscussoesControllerObterSerieDoAnalitoDoHistoricoQueryOptions(
        discussaoId,
        analito.analitoId,
      ),
    ),
  });

  const carregando = consultas.some((consulta) => consulta.isPending);
  const comErro = consultas.some((consulta) => consulta.isError);

  const series = consultas
    .map((consulta) => consulta.data)
    .filter((serie): serie is SerieDoAnalitoResponse => serie !== undefined);

  const serieSelecionada = series.find((serie) => serie.analitoId === selecionadoId);

  const titulo =
    modoEfetivo === 'exame'
      ? (selecionado?.exameNome ?? 'Evolução')
      : (serieSelecionada?.nome ?? 'Evolução');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SeletorDeAnalito analitos={analitos} valor={selecionadoId} aoEscolher={setAnalitoId} />

        {doExame.length > 1 && (
          <div
            role="group"
            aria-label="Como desenhar os resultados"
            className="flex rounded-md border border-input overflow-hidden self-start"
          >
            <BotaoDeModo
              ativo={modo === 'exame'}
              icone={BarChart3}
              rotulo={`Exame (${doExame.length} resultados)`}
              aoClicar={() => setModo('exame')}
            />
            <BotaoDeModo
              ativo={modo === 'analito'}
              icone={LineChart}
              rotulo="Só este exame"
              aoClicar={() => setModo('analito')}
            />
          </div>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">{titulo}</h3>

          {/* A tendência é de UM analito, então só aparece no modo do analito:
              anunciá-la ao lado do exame inteiro faria parecer que ela vale
              para todas as barras. */}
          {modoEfetivo === 'analito' && serieSelecionada?.tendencia && (
            <span className="text-xs text-muted-foreground">
              Da primeira à última medição:{' '}
              {serieSelecionada.tendencia.variacaoPercentual === null
                ? serieSelecionada.tendencia.direcao.toLowerCase()
                : `${
                    serieSelecionada.tendencia.variacaoPercentual > 0 ? '+' : ''
                  }${serieSelecionada.tendencia.variacaoPercentual.toLocaleString('pt-BR', {
                    maximumFractionDigits: 1,
                  })}%`}
            </span>
          )}
        </div>

        {carregando ? (
          <div className="p-6">
            <Skeleton className="h-56 rounded-md" />
          </div>
        ) : comErro || series.length === 0 ? (
          <EstadoErro
            aoTentarDeNovo={() => consultas.forEach((consulta) => void consulta.refetch())}
          />
        ) : (
          <div className="p-6 space-y-6">
            {modoEfetivo === 'exame' ? (
              <GraficoDeBarrasDoExame series={series} />
            ) : (
              serieSelecionada && <GraficoDeEvolucao serie={serieSelecionada} />
            )}

            {/* No modo exame, a tabela acompanha o gráfico: um bloco por
                analito das barras, não só o escolhido no seletor — senão a
                pessoa vê duas barras comparadas e só consegue conferir os
                números de uma. */}
            {modoEfetivo === 'exame'
              ? series.map((serie) => (
                  <div key={serie.analitoId}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {serie.nome}
                    </p>
                    <TabelaComparativa serie={serie} />
                  </div>
                ))
              : serieSelecionada && <TabelaComparativa serie={serieSelecionada} />}
          </div>
        )}
      </div>
    </div>
  );
}

function BotaoDeModo({
  ativo,
  icone: Icone,
  rotulo,
  aoClicar,
}: {
  ativo: boolean;
  icone: typeof BarChart3;
  rotulo: string;
  aoClicar: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      className={cn(
        'flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors',
        ativo ? 'bg-brand text-brand-foreground' : 'text-foreground hover:bg-muted',
      )}
    >
      <Icone className="w-4 h-4" aria-hidden="true" />
      {rotulo}
    </button>
  );
}
